import {styled, Tooltip, tooltipClasses, TooltipProps} from "@mui/material";
import React, {cloneElement, isValidElement, useRef, useState} from 'react';

interface HtmlTooltipProps extends TooltipProps {
    children: React.ReactElement;
    hideTooltipOnChildrenExpand?: boolean;
}

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip placement="top" {...props} classes={{ popper: className }} />
))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '111111',
        color: 'rgba(255, 255, 255, 0.87)',
        maxWidth: 300,
        fontSize: '16px',
        border: '0px',
    },
}));

const HtmlTooltip = ({ children, hideTooltipOnChildrenExpand = false, ...props }: HtmlTooltipProps) => {
    const [open, setOpen] = useState(false);
    const userInteractedRef = useRef(false);

    const handleOpen = () => {
        if (!userInteractedRef.current) {
            setOpen(true);
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleInteraction = () => {
        userInteractedRef.current = true;
        setOpen(false);
    };

    const resetInteraction = () => {
        userInteractedRef.current = false;
    };

    const childWithHandlers = isValidElement(children) && hideTooltipOnChildrenExpand
        ? cloneElement(children, {
            onFocus: (e: React.FocusEvent) => {
                e.stopPropagation();
                handleInteraction();
                const originalOnFocus = (children.props as any).onFocus;
                if (originalOnFocus) {
                    originalOnFocus(e);
                }
            },
            onClick: (e: React.MouseEvent) => {
                e.stopPropagation();
                handleInteraction();
                const originalOnClick = (children.props as any).onClick;
                if (originalOnClick) {
                    originalOnClick(e);
                }
            },
            onBlur: (e: any) => {
                e.stopPropagation();
                resetInteraction();
                const originalOnChange = (children.props as any).onBlur;
                if (originalOnChange) {
                    originalOnChange(e);
                }
            },
            onClose: (e: any) => {
                e.stopPropagation();
                resetInteraction();
                const originalOnClose = (children.props as any).onClose;
                if (originalOnClose) {
                    originalOnClose(e);
                }
            },
        } as any)
        : children;

    return (
        <StyledTooltip
            {...props}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
        >
            {childWithHandlers}
        </StyledTooltip>
    );
};

export default HtmlTooltip;